import React, { useCallback, useRef, useState } from 'react';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';

import { fg } from '@confluence/feature-gating';
import { useStartPagePdfExport } from '@confluence/page-pdf-export/entry-points/useStartPagePdfExport.ts';

export type ExportPdfMenuItemProps = {
	contentId: string;
	/** Content type of the page (e.g. `page`, `blogpost`); used to build the correct content ARI. Live docs are `page`. */
	contentType?: string;
	/** The legacy hidden-action export URL from the web item; used when the experiment is off or the backend selects a legacy (V1/V2) export path. */
	url: string;
	label?: string;
	onClickAnalytics?: () => void;
	closeParentMenu?: () => void;
};

export const ExportPdfMenuItem: React.FC<ExportPdfMenuItemProps> = ({
	contentId,
	contentType,
	url,
	label,
	onClickAnalytics,
	closeParentMenu,
}) => {
	// `isCreating` drives the item's disabled state (needs a re-render); `isCreatingRef` is the
	// in-flight guard read inside the click handler, so the handler doesn't depend on the state and
	// isn't recreated on every toggle.
	const [isCreating, setIsCreating] = useState(false);
	const isCreatingRef = useRef(false);

	// Shared create → route → open-modal flow (with a real "Try again"), also used by the Cmd/Ctrl+P
	// print shortcut. It owns the create analytics (`started` + `pagePdfExportCreate`), so this menu
	// item only drives the click and disabled state.
	const { startExport } = useStartPagePdfExport();

	const handleClick = useCallback(
		async (event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
			onClickAnalytics?.();

			if (!fg('cc_mohiti_pdf_export_kill_switch_gate')) {
				// Kill switch off: preserve the existing hidden-action URL behaviour (native <a href>)
				// and skip all GraphQL. Do NOT preventDefault so the anchor navigates as it does today.
				closeParentMenu?.();
				return;
			}

			event.preventDefault();

			if (isCreatingRef.current) {
				return;
			}
			isCreatingRef.current = true;
			setIsCreating(true);

			try {
				// The shared flow navigates (legacy) or opens the Export modal (V3 / error) with retry,
				// and fires the create analytics itself.
				await startExport({ contentId, contentType, url, source: 'menu' });

				closeParentMenu?.();
			} finally {
				// Always reset so an unexpected throw can't leave the item permanently disabled.
				isCreatingRef.current = false;
				setIsCreating(false);
			}
		},
		[onClickAnalytics, startExport, contentId, contentType, url, closeParentMenu],
	);

	return (
		<DropdownItem
			href={url}
			UNSAFE_shouldDisableRouterLink
			isDisabled={isCreating}
			testId="export-pdf-menu-item"
			onClick={(event) => {
				void handleClick(event);
			}}
		>
			{label}
		</DropdownItem>
	);
};
